'use strict';

/** @typedef {import('./pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage */

import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Gray } from 'metabux/theme/colors';
import { PipelineStatusGraph } from './components/PipelineStatusGraph';

/**
 * PipelineStatus boundary: consumes graph node view-models (`PipelineStatusStage[]`) only; React Flow is under `components/`.
 * @param {{ stages: PipelineStatusStage[]; isLoading?: boolean; error?: unknown }} props
 */
export function PipelineStatusPanel({ stages = [], isLoading, error }) {
	if (error) {
		return (
			<Alert severity="warning" sx={{ mt: 1 }}>
				Pipeline status could not be loaded.
			</Alert>
		);
	}

	if (isLoading && stages.length === 0) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
				<CircularProgress size={18} />
				<Typography variant="body2" color="text.secondary">
					Loading pipeline status…
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			className="PipelineStatusPanel"
			sx={{
				mt: 1.5,
				p: 2,
				border: 1,
				borderColor: 'divider',
				borderRadius: 1,
				bgcolor: 'background.paper',
			}}
		>
			<Typography
				variant="h4"
				sx={{
					fontWeight: 600,
					color: Gray[500],
					mb: 2,
					display: 'flex',
					alignItems: 'center',
					gap: 1,
				}}
			>
				Pipeline Status
			</Typography>
			<Typography component="div" variant="body1" sx={{ color: Gray[800], mb: 2 }}>
				Topology flows left to right from stages with no dependencies to terminal stages. Select a stage to open the side panel.
			</Typography>
			<PipelineStatusGraph stages={stages} />
		</Box>
	);
}
